import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  InvoiceCompanyClient,
  InvoiceNextDraftNumber,
  NewInvoice,
  TableInvoiceFields,
  TipologiaCassaCode,
  TipologiaCassaDescription,
} from '../../../../models/invoices';
import { FiscalProfileOutput } from '../../../../models/doctor-model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {
  CreateModifyInvoicesService,
  IvaOption,
  IvaOptionWithDescription,
} from '../create-modify-invoices.service';
import { LabelService } from '../../../../services/label.service';
import { ErrorPanelService } from '../../../../services/error-panel.service';
import { InvoicesService } from '../../../../services/invoices.service';
import { DoctorService } from '../../../../services/doctor.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import moment from 'moment';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmationPanelComponent } from '../../../../shared/confirmation-panel/confirmation-panel.component';
import { MatDialog } from '@angular/material/dialog';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

type ClientType = 'Parcella SDI' | 'Nota di Credito SDI' | null;

@Component({
  selector: 'app-panel-create-modify-invoice-sdi',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatTooltipModule,
  ],
  templateUrl: './panel-create-modify-invoice-sdi.component.html',
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  styleUrl: './panel-create-modify-invoice-sdi.component.scss',
})
export class PanelCreateModifyInvoiceSdiComponent implements OnInit {
  @Input() selectedInvoice: TableInvoiceFields | null = null;
  @Input() newInvoicePanelTitle: string = 'Nuova Fattura';
  @Input() doctorSDIErrorFields: string = '';
  @Output() cancel = new EventEmitter<void>();
  @Output() saveDraftSDI = new EventEmitter<NewInvoice>();
  @Output() modifyDraftSDI = new EventEmitter<NewInvoice>();
  @Output() saveReadySDI = new EventEmitter<NewInvoice>();
  @Output() modifyReadySDI = new EventEmitter<NewInvoice>();
  newInvoice!: NewInvoice;
  doctorFiscalProfile!: FiscalProfileOutput;
  invoiceForm!: FormGroup;
  displayedColumns = this.createModifyInvoicesService.displayedColumns;

  companyClients: InvoiceCompanyClient[] = [];

  readonly tipologiaCassaOptions = Object.entries(
    TipologiaCassaDescription
  ).map(([code, description]) => ({
    code,
    description,
  }));
  readonly ivaOptions = Object.values(IvaOptionWithDescription);
  defaultIvaOption = IvaOption.IVA_0_N1;
  filteredCompanyClients: InvoiceCompanyClient[] = [];
  private destroy$ = new Subject<void>();
  selectedClientType: ClientType = null;
  private isUpdatingBank = false;
  private isUpdatingIban = false;
  private modifiedBolloLineNaturaIVA!: IvaOption;
  private modifiedDescrizioneBolloLine!: string;

  constructor(
    public labelService: LabelService,
    private createModifyInvoicesService: CreateModifyInvoicesService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private invoicesService: InvoicesService,
    private doctorService: DoctorService,
    private errorPanelService: ErrorPanelService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.createForm();
    this.getExternalDataToPopulateFormFields();
  }

  createForm() {
    this.invoiceForm = this.createModifyInvoicesService.createInvoiceForm();
    this.invoiceForm.get('cassa_previdenza')?.setValue(true);
    this.invoiceForm.get('cassa_previdenza')?.disable();
    this.createSubscriptionsToFormFields();
  }

  createSubscriptionsToFormFields() {
    this.createSubscriptionsToFieldTipo();
    this.createSubscriptionsToFieldPagamento();
    this.createSubscriptionsToBankAndIban();
    this.createSubscriptionsToFieldsCassaPrevidenza();
  }

  createSubscriptionsToFieldTipo() {
    this.invoiceForm
      .get('tipo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectedClientType = value;
        // if tipo contains a value, enable all the other fields
        const controls = Object.keys(this.invoiceForm.controls).filter(
          (key) => key !== 'tipo'
        );
        if (value) {
          controls.forEach((controlName) => {
            const control = this.invoiceForm.get(controlName);
            if (control && !(control instanceof FormArray)) {
              control.enable();
            }
          });
        }
      });
  }
  createSubscriptionsToFieldPagamento() {
    this.invoiceForm
      .get('pagamento')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value === 'Bonifico') {
          this.invoiceForm
            .get('doctor_bank_name')
            ?.setValidators([Validators.required]);
          this.invoiceForm
            .get('doctor_bank_iban')
            ?.setValidators([Validators.required]);
        } else {
          this.invoiceForm.get('doctor_bank_name')?.clearValidators();
          this.invoiceForm.get('doctor_bank_iban')?.clearValidators();
        }
        this.invoiceForm.get('doctor_bank_name')?.updateValueAndValidity();
        this.invoiceForm.get('doctor_bank_iban')?.updateValueAndValidity();
      });
  }
  createSubscriptionsToBankAndIban() {
    this.invoiceForm
      .get('doctor_bank_name')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.isUpdatingBank) return;

        const index = this.bankNameOptions.findIndex(
          (option) => option === value
        );
        if (index !== -1) {
          this.isUpdatingIban = true;
          this.invoiceForm
            .get('doctor_bank_iban')
            ?.setValue(this.bankIbanOptions[index] || '');
          this.isUpdatingIban = false;
        }
      });

    this.invoiceForm
      .get('doctor_bank_iban')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.isUpdatingIban) return;

        const index = this.bankIbanOptions.findIndex(
          (option) => option === value
        );
        if (index !== -1) {
          this.isUpdatingBank = true;
          this.invoiceForm
            .get('doctor_bank_name')
            ?.setValue(this.bankNameOptions[index] || '');
          this.isUpdatingBank = false;
        }
      });
  }
  createSubscriptionsToFieldsCassaPrevidenza() {
    this.invoiceForm.patchValue({
      tipologia_cassa: TipologiaCassaCode.TC21,
      percentuale_cassa: '2',
      cassa_previdenza_codice_iva: this.defaultIvaOption,
    });

    this.invoiceForm
      .get('percentuale_cassa')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateInvoiceTotals();
      });

    this.invoiceForm
      .get('tipologia_cassa')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateInvoiceTotals();
      });

    this.invoiceForm
      .get('escludi_bollo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleBolloLine();
      });

    this.invoiceForm
      .get('cassa_previdenza_codice_iva')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateInvoiceTotals();
      });
  }

  getExternalDataToPopulateFormFields() {
    this.getSDINextNumber();
    this.loadCompanyClients();
    this.getDoctorFiscalProfile();
  }

  getSDINextNumber() {
    this.invoicesService.getSDINextNumber().subscribe({
      next: (res: InvoiceNextDraftNumber) => {
        this.invoiceForm
          .get('progressivo_numero')
          ?.setValue(res.progressivo_numero.split('/SDI')[0]);
        this.invoiceForm
          .get('progressivo_anno')
          ?.setValue(res.progressivo_anno);
      },
      error: (error) => {
        console.error('Error loading next number:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento del numero progressivo'
        );
      },
    });
  }
  loadCompanyClients() {
    this.invoicesService.getInvoicesCompanyClients().subscribe({
      next: (clients) => {
        this.companyClients = clients.sort((a, b) =>
          a.company_name.localeCompare(b.company_name)
        );
        this.filteredCompanyClients = this.companyClients;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading company clients:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento dei clienti aziendali'
        );
      },
    });
  }
  getDoctorFiscalProfile() {
    this.doctorService.getFiscalProfile().subscribe({
      next: (res) => {
        this.doctorFiscalProfile = res;
        switch (this.doctorFiscalProfile.regime_fiscale) {
          case 'RF01':
            this.defaultIvaOption = IvaOption.IVA_0_N4;
            break;
          case 'RF02':
          case 'RF19':
            this.defaultIvaOption = IvaOption.IVA_0_N2_2;
            break;
        }
        if (!this.selectedInvoice) {
          this.invoiceForm.patchValue({
            cassa_previdenza_codice_iva: this.defaultIvaOption,
          });
        } else {
          this.getInvoiceById();
        }
      },
      error: (error) => {
        console.error('Error loading doctor info:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento dei dati del professionista'
        );
      },
    });
  }
  getInvoiceById() {
    this.invoicesService
      .getSDIInvoiceById(this.selectedInvoice?.id || '')
      .subscribe({
        next: (invoice: NewInvoice) => {
          this.newInvoice = invoice;
          this.populateForm();
        },
        error: (error) => {
          console.error('Error loading invoice:', error);
          this.errorPanelService.showErrorSnackBar(
            'Errore durante il caricamento dei dati della fattura'
          );
        },
      });
  }

  showAllClients(): void {
    this.filteredCompanyClients = this.companyClients;
  }
  filterClients(event: any): void {
    if (this.invoiceForm.get('client_id')?.value !== null) {
      this.invoiceForm.get('client_id')?.patchValue(null);
    }
    const searchTerm = event.target.value.toLowerCase();

    this.filteredCompanyClients = this.companyClients.filter((client) =>
      client.company_name.toLowerCase().includes(searchTerm)
    );
  }
  selectClient(client: InvoiceCompanyClient): void {
    this.invoiceForm.patchValue({
      client_id: client.id,
    });
    this.invoiceForm.get('cliente')?.patchValue(`${client.company_name}`);
  }
  onClientBlur() {
    if (this.invoiceForm.get('client_id')?.value === null) {
      this.invoiceForm.get('cliente')?.patchValue(null);
    }
  }

  private populateForm() {
    if (this.newInvoice) {
      let formattedDate = null;
      if (this.newInvoice.data) {
        formattedDate = moment(this.newInvoice.data, 'DD/MM/YYYY').toDate();
      }

      let cassa_previdenza_codice_iva = null;
      if (this.newInvoice.cassa_previdenza_natura_iva) {
        cassa_previdenza_codice_iva =
          '0% - ' + this.newInvoice.cassa_previdenza_natura_iva;
      } else {
        cassa_previdenza_codice_iva = this.newInvoice.cassa_previdenza_iva
          ? this.newInvoice.cassa_previdenza_iva + '%'
          : null;
      }

      this.invoiceForm.patchValue({
        tipo: this.newInvoice.tipo || null,
        client_id: this.newInvoice.client_id || null,
        cliente: this.selectedInvoice?.client_name || null,
        riferimento: this.newInvoice.riferimento || null,
        progressivo_numero: this.formatDisplayNumber(
          this.newInvoice.progressivo_numero || ''
        ),
        progressivo_anno: this.newInvoice.progressivo_anno || null,
        data: formattedDate,
        pagata: this.newInvoice.pagata || false,
        scadenza: this.newInvoice.scadenza || null,
        pagamento: this.newInvoice.pagamento || null,
        cassa_previdenza: this.newInvoice.cassa_previdenza || false,
        tipologia_cassa: this.newInvoice.tipologia_cassa || null,
        percentuale_cassa: this.newInvoice.percentuale_cassa || null,
        cassa_previdenza_codice_iva: cassa_previdenza_codice_iva,
        imponibile: this.newInvoice.imponibile || null,
        totale_cassa_previdenza:
          this.newInvoice.totale_cassa_previdenza || null,
        imposta: this.newInvoice.imposta || 0,
        bollo: this.newInvoice.bollo || null,
        escludi_bollo: this.newInvoice.escludi_bollo || false,
        applica_bollo: this.newInvoice.applica_bollo || false,
        totale: this.newInvoice.totale || null,
        doctor_bank_name: this.newInvoice.doctor_bank_name || null,
        doctor_bank_iban: this.newInvoice.doctor_bank_iban || null,
        totale_ritenuta: this.newInvoice.totale_ritenuta || null,
      });

      while (this.documentLinesFormArray.length) {
        this.documentLinesFormArray.removeAt(0);
      }

      if (this.newInvoice.linee_documento) {
        this.newInvoice.linee_documento.forEach((line) => {
          // Skip the line if it's a "Bollo" entry, it will be added automatically
          if (line.descrizione.startsWith('Imposta di Bollo')) {
            this.modifiedBolloLineNaturaIVA =
              Object.values(IvaOption).find((option) =>
                option.includes(line.natura_iva)
              ) || this.defaultIvaOption;
            this.modifiedDescrizioneBolloLine = line.descrizione;
            this.createModifyInvoicesService.updateBolloLine(
              this.documentLinesFormArray,
              this.modifiedBolloLineNaturaIVA,
              this.modifiedDescrizioneBolloLine
            );
            return;
          }
          let ivaOption: string;
          if (line.natura_iva) {
            ivaOption = `0% - ${line.natura_iva}`;
          } else {
            ivaOption = `${line.iva}%`;
          }

          const lineGroup =
            this.createModifyInvoicesService.createDocumentLine();
          lineGroup.patchValue({
            descrizione: line.descrizione || null,
            quantita: line.quantita || '1.00',
            prezzo: line.prezzo || '0.00',
            sconto: line.sconto || '0',
            iva_option: ivaOption,
          });

          this.documentLinesFormArray.push(lineGroup);
          this.setupLineCalculations(lineGroup);
        });
      }
    }
  }

  private formatDisplayNumber(value: string): string {
    if (!value) return '';
    return value.replace('/SDI', '');
  }

  private setupLineCalculations(lineGroup: FormGroup) {
    this.createModifyInvoicesService.setupLineCalculations(lineGroup, () => {
      this.calculateInvoiceTotals();
      this.handleBolloLine();
    });
  }

  private handleBolloLine() {
    const bolloIvaOption =
      this.doctorFiscalProfile.regime_fiscale === 'RF01'
        ? IvaOption.IVA_0_N1
        : this.defaultIvaOption;
    this.createModifyInvoicesService.handleBolloLine(
      this.documentLinesFormArray,
      this.invoiceForm.get('escludi_bollo')?.value || false,
      this.invoiceForm,
      this.modifiedBolloLineNaturaIVA
        ? this.modifiedBolloLineNaturaIVA
        : bolloIvaOption,
      this.doctorFiscalProfile.regime_fiscale || undefined
    );
    this.calculateInvoiceTotals();
  }

  // FIXME: if i just call the one in the service it doesn't update the form. For now i leave the entire method here
  private calculateInvoiceTotals() {
    this.createModifyInvoicesService.updateFormWithCalculatedTotals(
      this.documentLinesFormArray,
      this.invoiceForm,
      this.doctorFiscalProfile?.regime_fiscale || undefined
    );
  }

  removeDocumentLine(index: number) {
    this.createModifyInvoicesService.removeDocumentLine(
      index,
      this.documentLinesFormArray,
      this.invoiceForm
    );
    this.calculateInvoiceTotals();
  }

  addDocumentLine() {
    this.createModifyInvoicesService.addDocumentLine(
      this.documentLinesFormArray,
      this.fieldsDisabled,
      this.defaultIvaOption,
      () => {
        this.calculateInvoiceTotals();
        this.handleBolloLine();
      }
    );
  }

  onCancel() {
    this.cancel.emit();
  }

  onSaveDispatcher(type: 'draft' | 'ready') {
    this.markFormGroupTouched(this.invoiceForm);

    if (this.invoiceForm.valid) {
      const formValue = this.invoiceForm.getRawValue();

      let updatedInvoice: NewInvoice = {
        ...this.newInvoice,
        ...formValue,
        progressivo_numero: `${formValue.progressivo_numero}/SDI`,
        linee_documento: formValue.linee_documento.map((line: any) => {
          const ivaOption = line.iva_option;
          let iva: string;
          let natura_iva: string | null;

          if (ivaOption.includes('-')) {
            iva = '0';
            natura_iva = ivaOption.split('- ')[1];
          } else {
            iva = ivaOption.replace('%', '');
            natura_iva = null;
          }

          delete line.iva_option;

          return {
            ...line,
            quantita: parseFloat(line.quantita),
            prezzo: parseFloat(line.prezzo),
            sconto: parseFloat(line.sconto),
            totale: parseFloat(line.totale),
            iva,
            natura_iva,
          };
        }),
      };

      if (updatedInvoice.data) {
        updatedInvoice.data = moment(updatedInvoice.data).format('DD/MM/YYYY');
      }

      if (formValue.cassa_previdenza && formValue.cassa_previdenza_codice_iva) {
        if (formValue.cassa_previdenza_codice_iva.includes('-')) {
          updatedInvoice.cassa_previdenza_iva = 0;
          updatedInvoice.cassa_previdenza_natura_iva =
            formValue.cassa_previdenza_codice_iva.split('- ')[1];
        } else {
          updatedInvoice.cassa_previdenza_iva = parseInt(
            formValue.cassa_previdenza_codice_iva.replace('%', '')
          );
          updatedInvoice.cassa_previdenza_natura_iva = null;
        }
      }
      if (
        formValue.cassa_previdenza_codice_iva === null ||
        formValue.cassa_previdenza_codice_iva === undefined
      ) {
        updatedInvoice.cassa_previdenza_iva = null;
        updatedInvoice.cassa_previdenza_natura_iva = null;
      }

      if ('stato' in updatedInvoice) {
        delete updatedInvoice.stato;
      }
      if ('cassa_previdenza_codice_iva' in updatedInvoice) {
        delete updatedInvoice.cassa_previdenza_codice_iva;
      }
      if ('flag_opposizione' in updatedInvoice) {
        delete updatedInvoice.flag_opposizione;
      }

      if (type === 'draft') {
        this.onSaveDraft(updatedInvoice);
      } else {
        this.onSaveReady(updatedInvoice);
      }
    }
  }

  onSaveDraft(updatedInvoice: NewInvoice) {
    if (this.selectedInvoice) {
      this.modifyDraftSDI.emit(updatedInvoice);
    } else {
      this.saveDraftSDI.emit(updatedInvoice);
    }
  }

  onSaveReady(updatedInvoice: NewInvoice) {
    if (this.selectedInvoice) {
      this.modifyReadySDI.emit(updatedInvoice);
    } else {
      this.saveReadySDI.emit(updatedInvoice);
    }
  }

  /**
   * Recursively marks all controls in a form group as 'touched'
   * This method is needed to trigger validation visual feedback when saving/submitting forms.
   * Without it, invalid fields may not show error states until the user manually interacts with them.
   */
  markFormGroupTouched(formGroup: FormGroup) {
    this.createModifyInvoicesService.markFormGroupTouched(formGroup);
  }

  get bankNameOptions(): string[] {
    const options: string[] = [];
    if (this.doctorFiscalProfile?.bank_name) {
      options.push(this.doctorFiscalProfile.bank_name);
    }
    if (this.doctorFiscalProfile?.bank_name_2) {
      options.push(this.doctorFiscalProfile.bank_name_2);
    }
    return options;
  }

  get bankIbanOptions(): string[] {
    const options: string[] = [];
    if (this.doctorFiscalProfile?.iban) {
      options.push(this.doctorFiscalProfile.iban);
    }
    if (this.doctorFiscalProfile?.iban_2) {
      options.push(this.doctorFiscalProfile.iban_2);
    }
    return options;
  }

  get documentLinesFormArray() {
    return this.invoiceForm.get('linee_documento') as FormArray;
  }

  get fieldsDisabled(): boolean {
    return !this.invoiceForm?.get('tipo')?.value;
  }

  get canSaveDraft(): boolean {
    return this.invoiceForm.valid;
  }
}
